import React from 'react';

import { Link } from '../../../components/atoms/link/link';

type TokenReplacements = {
  optionalOrgCopy?: string;
  orgName: string;
  orgNameAbbreviation?: string;
  givenName?: string;
  psyomicsPrivacyPolicyUrl?: string;
  orgPrivacyPolicyUrl?: string;
};

function replaceTokens(text: string, replacements: TokenReplacements): string {
  let processedText = text;

  if (replacements.givenName === undefined) {
    processedText = processedText.replace(/\s*\{givenName\}/g, '');
  }

  const effectiveReplacements = {
    ...replacements,
    orgNameAbbreviation: replacements.orgNameAbbreviation ?? replacements.orgName,
  };

  Object.keys(effectiveReplacements).forEach((key) => {
    const value = effectiveReplacements[key as keyof TokenReplacements];
    if (value !== undefined) {
      processedText = processedText.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
  });

  return processedText;
}

function parseLinksInText(text: string, replacements: TokenReplacements): React.ReactNode {
  const linkRegex = /\[([^\]]+)\]\((['"]?)([^'")\s]+)\2\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    const [fullMatch, linkText, quote, tokenOrUrl] = match;
    const matchIndex = match.index;

    if (matchIndex > lastIndex) parts.push(text.substring(lastIndex, matchIndex));

    const href = quote ? tokenOrUrl : replacements[tokenOrUrl as keyof TokenReplacements];

    if (href) {
      parts.push(
        <Link className="text-typography-light underline" href={href} key={matchIndex} target="_blank">
          {linkText}
        </Link>
      );
    } else {
      parts.push(fullMatch);
    }

    lastIndex = matchIndex + fullMatch.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  if (parts.length === 0) {
    return text;
  } else {
    return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
  }
}

function parseTextWithTokens(text: string, replacements: TokenReplacements): React.ReactNode {
  const textWithReplacedTokens = replaceTokens(text, replacements);
  return parseLinksInText(textWithReplacedTokens, replacements);
}

export { parseTextWithTokens };
export type { TokenReplacements };
